import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Zap,
  AlertCircle,
  Smartphone,
  ShieldCheck,
  Copy,
  Check,
  ArrowRight,
  Palette,
  FileText,
  Sparkles,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { useAuthContext } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { apiService } from '../../services/api';

type Step =
  | 'credentials'
  | 'mfa-challenge' // TOTP (authenticator app)
  | 'mfa-email' // EMAIL_OTP / SMS_MFA — code sent to a destination
  | 'mfa-select' // SELECT_MFA_TYPE — user picks a method
  | 'mfa-setup'
  | 'new-password';

const MFA_TYPE_LABELS: Record<string, string> = {
  SOFTWARE_TOKEN_MFA: 'אפליקציית אימות (TOTP)',
  EMAIL_OTP: 'קוד במייל',
  SMS_MFA: 'קוד ב-SMS',
};

const W3LoginPage: React.FC = () => {
  const { loginWithW3, isLoading } = useAuthContext();
  const { bgColor, setBgColor, options: bgOptions } = useTheme();
  const navigate = useNavigate();

  // Step machine
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Carried between steps
  const [session, setSession] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  // MFA method state — which method the current code belongs to, the masked
  // destination for emailed/SMS codes, and the options for SELECT_MFA_TYPE.
  const [activeMfaType, setActiveMfaType] = useState<string>('SOFTWARE_TOKEN_MFA');
  const [destination, setDestination] = useState<string | null>(null);
  const [availableMfaTypes, setAvailableMfaTypes] = useState<string[]>([]);

  // MFA setup specifics
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [associating, setAssociating] = useState(false);

  // Autofocus the active step's first input
  const codeInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if ((step === 'mfa-challenge' || step === 'mfa-email' || step === 'mfa-setup') && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  // Centralized error translator for Hebrew RTL UI.
  const friendlyError = (err: any): string => {
    const detail = err?.response?.data?.detail || err?.response?.data?.message || '';
    const lower = String(detail).toLowerCase();
    if (lower.includes('code_expired')) {
      return 'הקוד פג תוקף. בקש קוד חדש ונסה שוב.';
    }
    if (lower.includes('invalid_code') || lower.includes('verify your code')) {
      return 'הקוד שגוי. ודא שהזנת את הקוד הנכון ונסה שוב.';
    }
    if (lower.includes('mfa_challenge_failed') || lower.includes('mfa verification')) {
      return 'אימות ה-MFA נכשל. ודא שהזנת את הקוד הנוכחי.';
    }
    if (
      lower.includes('expired_session') ||
      lower.includes('invalid_session') ||
      lower.includes('session is invalid')
    ) {
      return 'פג תוקף ההתחברות. נא להתחיל מחדש.';
    }
    if (lower.includes('invalid_mfa_selection')) {
      return 'שיטת האימות שנבחרה אינה זמינה. נסה שוב.';
    }
    if (lower.includes('invalid_mfa_request')) {
      return 'בקשת האימות אינה תקינה. נסה שוב או התחבר מחדש.';
    }
    if (lower.includes('rate_limited') || lower.includes('too many')) {
      return 'יותר מדי ניסיונות. נסה שוב בעוד דקה.';
    }
    if (lower.includes('not supported')) {
      return 'סוג MFA זה לא נתמך. פנה למנהל המערכת.';
    }
    return detail || 'אימייל או סיסמה שגויים';
  };

  const resetToCredentials = (toastMsg?: string) => {
    setStep('credentials');
    setSession(null);
    setMfaCode('');
    setNewPassword('');
    setNewPasswordConfirm('');
    setOtpauthUrl('');
    setSecretCode('');
    setSecretCopied(false);
    setActiveMfaType('SOFTWARE_TOKEN_MFA');
    setDestination(null);
    setAvailableMfaTypes([]);
    setError('');
    if (toastMsg) toast.error(toastMsg);
  };

  // Kick off TOTP enrollment so the QR is ready on the mfa-setup screen.
  const runAssociate = async (challengeSession: string) => {
    setAssociating(true);
    try {
      const assoc = await apiService.w3MfaAssociate({ email: email.trim(), session: challengeSession });
      setOtpauthUrl(assoc.otpauth_url);
      setSecretCode(assoc.secret_code);
      setSession(assoc.session);
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setAssociating(false);
    }
  };

  // Central challenge router — every login response that carries a challenge
  // funnels through here so credentials / new-password / select all behave
  // identically. Returns nothing; sets the step + carried state.
  const routeChallenge = async (result: Extract<Awaited<ReturnType<typeof loginWithW3>>, { kind: 'challenge' }>) => {
    setSession(result.session);
    setDestination(result.destination ?? null);
    setAvailableMfaTypes(result.availableMfaTypes ?? []);
    setMfaCode('');
    switch (result.challenge) {
      case 'SOFTWARE_TOKEN_MFA':
        setActiveMfaType('SOFTWARE_TOKEN_MFA');
        setStep('mfa-challenge');
        break;
      case 'EMAIL_OTP':
      case 'SMS_MFA':
        setActiveMfaType(result.challenge);
        setStep('mfa-email');
        break;
      case 'SELECT_MFA_TYPE':
        setStep('mfa-select');
        break;
      case 'NEW_PASSWORD_REQUIRED':
        setStep('new-password');
        break;
      case 'MFA_SETUP':
        setStep('mfa-setup');
        await runAssociate(result.session);
        break;
      default:
        setError('סוג challenge לא נתמך: ' + String(result.challenge));
    }
  };

  const onSuccess = (kind: 'success' | 'challenge') => {
    if (kind === 'success') {
      toast.success('התחברת בהצלחה');
      navigate('/', { replace: true });
    }
  };

  // ── Step 1: credentials ──
  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) return setError('נדרש אימייל');
    if (!password) return setError('נדרשת סיסמה');

    setIsSubmitting(true);
    try {
      const result = await loginWithW3({ email: email.trim(), password });
      if (result.kind === 'success') {
        onSuccess('success');
        return;
      }
      await routeChallenge(result);
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step 2: submit an MFA code (TOTP / EMAIL_OTP / SMS) ──
  // Shared by the authenticator-app screen and the emailed/SMS-code screen;
  // `activeMfaType` tells the Gateway which Cognito challenge to answer.
  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (mfaCode.length !== 6) return setError('הזן קוד בן 6 ספרות');
    if (!session) return resetToCredentials('פג תוקף ההתחברות. נא להתחיל מחדש.');

    setIsSubmitting(true);
    try {
      const result = await loginWithW3({
        email: email.trim(),
        session,
        mfaCode,
        mfaType: activeMfaType,
      });
      if (result.kind === 'success') {
        onSuccess('success');
      } else {
        // Chained challenge (rare) — route it.
        await routeChallenge(result);
      }
    } catch (err: any) {
      const detail = String(err?.response?.data?.detail || '');
      if (/expired_session|invalid_session/i.test(detail)) {
        resetToCredentials('פג תוקף ההתחברות. נא להתחיל מחדש.');
      } else {
        setError(friendlyError(err));
        setMfaCode('');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── SELECT_MFA_TYPE: user picked a method → fetch that challenge ──
  const handleSelectMfaType = async (choice: string) => {
    setError('');
    if (!session) return resetToCredentials('פג תוקף ההתחברות. נא להתחיל מחדש.');
    setIsSubmitting(true);
    try {
      const result = await loginWithW3({ email: email.trim(), session, mfaSelection: choice });
      if (result.kind === 'success') {
        onSuccess('success');
      } else {
        await routeChallenge(result);
      }
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Resend an emailed/SMS code by re-running the credentials login ──
  const handleResendCode = async () => {
    setError('');
    if (!password) return resetToCredentials('פג תוקף ההתחברות. נא להתחיל מחדש.');
    setIsSubmitting(true);
    try {
      const result = await loginWithW3({ email: email.trim(), password });
      if (result.kind === 'challenge') {
        await routeChallenge(result);
        toast.success('קוד חדש נשלח');
      }
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step 3: TOTP setup (first-time enrollment) ──
  const handleMfaSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (mfaCode.length !== 6) return setError('הזן קוד בן 6 ספרות מהאפליקציה');
    if (!session) return resetToCredentials('פג תוקף ההתחברות. נא להתחיל מחדש.');

    setIsSubmitting(true);
    try {
      // 1. Verify the TOTP code → enables MFA on the user
      const verify = await apiService.w3MfaVerify({ session, userCode: mfaCode });
      const nextSession = verify.session || session;
      // 2. Re-call /w3-login with session+mfaCode to receive actual tokens
      const result = await loginWithW3({ email: email.trim(), session: nextSession, mfaCode });
      if (result.kind === 'success') {
        toast.success('האימות הדו-שלבי הופעל בהצלחה');
        onSuccess('success');
      } else {
        // Chained — unlikely; surface the message
        setError(friendlyError({ response: { data: { message: result.message } } }));
      }
    } catch (err: any) {
      if (err?.response?.status === 401 && /invalid_session/i.test(err?.response?.data?.detail || '')) {
        resetToCredentials('פג תוקף ההתחברות. נא להתחיל מחדש.');
      } else {
        setError(friendlyError(err));
        setMfaCode('');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step 4: NEW_PASSWORD_REQUIRED ──
  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) return setError('סיסמה חדשה חייבת להיות באורך 8 תווים לפחות');
    if (newPassword !== newPasswordConfirm) return setError('הסיסמאות אינן תואמות');
    if (!session) return resetToCredentials('פג תוקף ההתחברות. נא להתחיל מחדש.');

    setIsSubmitting(true);
    try {
      const result = await loginWithW3({ email: email.trim(), session, newPassword });
      if (result.kind === 'success') {
        toast.success('הסיסמה עודכנה והתחברת בהצלחה');
        onSuccess('success');
      } else {
        // Cognito may chain into any MFA challenge after a password change.
        await routeChallenge(result);
      }
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secretCode);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch {
      toast.error('לא ניתן להעתיק — סמן ידנית');
    }
  };

  // Arrow-key navigation for the palette radiogroup (WAI-ARIA radio pattern):
  // arrows move selection AND focus, wrapping at the ends. The page is RTL, so
  // horizontal arrows are mirrored (Left = next, Right = previous); vertical
  // arrows stay logical (Down = next, Up = previous).
  const handleSwatchKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const isNext = e.key === 'ArrowLeft' || e.key === 'ArrowDown';
    const isPrev = e.key === 'ArrowRight' || e.key === 'ArrowUp';
    if (!isNext && !isPrev) return;
    e.preventDefault();
    const count = bgOptions.length;
    const nextIndex = isNext ? (index + 1) % count : (index - 1 + count) % count;
    setBgColor(bgOptions[nextIndex].id);
    const buttons = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    buttons?.[nextIndex]?.focus();
  };

  // ── Loading boot state ──
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--accent)] border-t-transparent" />
      </div>
    );
  }

  // Active palette → drives the login accent + hero gradient. Picking a swatch
  // re-colors the whole login (button, icon chips, hero) instantly.
  const theme = bgOptions.find((o) => o.id === bgColor) ?? bgOptions[0];

  // Shared light-theme form styling (the form sits on a white panel now).
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';
  const inputClass =
    'block w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] text-sm transition-colors';
  const codeInputClass =
    'block w-full text-center text-2xl tracking-[0.5em] py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] font-mono';
  const primaryBtn =
    'w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium text-sm text-white shadow-sm transition-[transform,background-color,box-shadow] duration-150 ease-out-emil active:scale-[0.98] disabled:active:scale-100 bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--accent)]';
  const linkBtn = 'w-full text-sm text-gray-500 hover:text-gray-800 transition-colors';
  const errorBox =
    'flex items-center gap-2 text-error-600 text-sm bg-error-50 border border-error-200 rounded-lg px-3 py-2';
  const stepIconWrap =
    'inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 text-[var(--accent)] mb-3';

  const HERO_FEATURES = [
    { icon: <Lock className="h-5 w-5" />, title: 'אבטחה מלאה (Zero-PII)', desc: 'כל עיבוד עובר דרך W3 Gateway עם בידוד מידע אישי' },
    { icon: <FileText className="h-5 w-5" />, title: 'ניהול מסמכים חכם', desc: 'OCR מתקדם, זיהוי טבלאות, ומעקב גרסאות' },
    { icon: <Sparkles className="h-5 w-5" />, title: 'בינה מלאכותית פיננסית', desc: 'ניתוח דוחות, חישובים פיננסיים, והמלצות פעולה' },
  ];

  return (
    <div
      className="min-h-screen flex bg-white"
      dir="rtl"
      style={{ '--accent': theme.accent, '--accent-hover': theme.accentHover } as React.CSSProperties}
    >
      {/* Brand hero — right side in RTL (first child). Hidden on < lg.
          Gradient re-colors with the selected palette. */}
      <div
        className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 text-white overflow-hidden"
        style={{ backgroundImage: `linear-gradient(to bottom right, ${theme.hero[0]}, ${theme.hero[1]}, ${theme.hero[2]})` }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -left-24 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -right-24 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>

        {/* Brand mark */}
        <div className="relative flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <span className="text-lg font-semibold tracking-tight">Financial AI</span>
        </div>

        {/* Headline + feature cards */}
        <div className="relative">
          <h1 className="text-4xl font-bold leading-tight">Financial&nbsp;AI&nbsp;OS</h1>
          <p className="mt-3 text-lg text-white/80">סוכן בינה מלאכותית לניתוח דוחות ומסמכים פיננסיים</p>
          <p className="mt-1.5 text-sm text-white/55">משכנתאות · שמאות · דוחות בנק · חוזים — הכל במקום אחד</p>

          <div className="mt-8 space-y-3">
            {HERO_FEATURES.map((f) => (
              <div
                key={f.title}
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-4 py-3.5"
              >
                <div
                  className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl text-white shadow-sm"
                  style={{ backgroundColor: theme.accent }}
                >
                  {f.icon}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{f.title}</div>
                  <div className="text-xs text-white/60 mt-0.5">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-xs text-white/45">© 2026 W3 Financial AI · Powered by W3 Platform</div>
      </div>

      {/* Form — left side. Full width on mobile. */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-10 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile brand (hero is hidden on small screens) */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl text-white"
              style={{ backgroundColor: theme.accent }}
            >
              <Zap className="h-5 w-5" />
            </div>
            <span className="text-lg font-bold text-gray-900">Financial AI</span>
          </div>

          {/* Step 1 — credentials */}
          {step === 'credentials' && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">ברוכים הבאים</h2>
                <p className="text-sm text-gray-500 mt-1.5">התחברו למערכת Financial AI</p>
              </div>

              <form onSubmit={handleCredentials} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="email" className={labelClass}>
                    דוא״ל
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError('');
                      }}
                      className={`${inputClass} pr-10`}
                      placeholder="name@company.com"
                      autoFocus
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className={labelClass}>
                    סיסמה
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError('');
                      }}
                      className={`${inputClass} pr-10 pl-10`}
                      placeholder="הזינו סיסמה"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className={errorBox}>
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" disabled={isSubmitting || !email.trim() || !password} className={primaryBtn}>
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>מתחבר...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4" />
                      <span>התחברות</span>
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {/* Step 2 — TOTP challenge (already enrolled) */}
          {step === 'mfa-challenge' && (
            <>
              <div className="text-center mb-5">
                <div className={stepIconWrap}>
                  <Smartphone className="h-6 w-6" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">הזן קוד אימות מהאפליקציה</h2>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  פתח את אפליקציית ה-Authenticator (Google Authenticator / Authy / 1Password)
                  והעתק את הקוד הנוכחי עבור <span className="font-medium text-gray-700">Financial AI</span>.
                </p>
              </div>

              <form onSubmit={handleSubmitCode} className="space-y-5" noValidate>
                <div>
                  <label htmlFor="mfa-code" className={labelClass}>
                    קוד אימות (6 ספרות)
                  </label>
                  <input
                    ref={codeInputRef}
                    id="mfa-code"
                    type="text"
                    value={mfaCode}
                    onChange={(e) => {
                      setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setError('');
                    }}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    dir="ltr"
                    className={codeInputClass}
                    placeholder="••••••"
                  />
                </div>

                {error && (
                  <div className={errorBox}>
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" disabled={isSubmitting || mfaCode.length !== 6} className={primaryBtn}>
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>מאמת...</span>
                    </>
                  ) : (
                    <>
                      <ArrowRight className="h-4 w-4" />
                      <span>התחבר</span>
                    </>
                  )}
                </button>

                <button type="button" onClick={() => resetToCredentials()} className={linkBtn}>
                  בטל וחזור לכניסה
                </button>
              </form>
            </>
          )}

          {/* Step 2b — emailed / SMS one-time code */}
          {step === 'mfa-email' && (
            <>
              <div className="text-center mb-5">
                <div className={stepIconWrap}>
                  <Mail className="h-6 w-6" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {activeMfaType === 'SMS_MFA' ? 'הזן קוד מה-SMS' : 'הזן קוד מהמייל'}
                </h2>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  שלחנו קוד חד-פעמי
                  {destination ? (
                    <> אל <span className="font-medium text-gray-700" dir="ltr">{destination}</span></>
                  ) : null}
                  . הקוד תקף למספר דקות.
                </p>
              </div>

              <form onSubmit={handleSubmitCode} className="space-y-5" noValidate>
                <div>
                  <label htmlFor="mfa-email-code" className={labelClass}>
                    קוד אימות (6 ספרות)
                  </label>
                  <input
                    ref={codeInputRef}
                    id="mfa-email-code"
                    type="text"
                    value={mfaCode}
                    onChange={(e) => {
                      setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setError('');
                    }}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    dir="ltr"
                    className={codeInputClass}
                    placeholder="••••••"
                  />
                </div>

                {error && (
                  <div className={errorBox}>
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" disabled={isSubmitting || mfaCode.length !== 6} className={primaryBtn}>
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>מאמת...</span>
                    </>
                  ) : (
                    <>
                      <ArrowRight className="h-4 w-4" />
                      <span>התחבר</span>
                    </>
                  )}
                </button>

                <button type="button" onClick={handleResendCode} disabled={isSubmitting} className="w-full text-sm text-[var(--accent)] hover:opacity-80 transition-opacity disabled:opacity-50">
                  לא קיבלת קוד? שלח שוב
                </button>
                <button type="button" onClick={() => resetToCredentials()} className={linkBtn}>
                  בטל וחזור לכניסה
                </button>
              </form>
            </>
          )}

          {/* Step 2c — choose an MFA method */}
          {step === 'mfa-select' && (
            <>
              <div className="text-center mb-5">
                <div className={stepIconWrap}>
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">בחר שיטת אימות</h2>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  לחשבון שלך מוגדרות כמה שיטות אימות דו-שלבי. בחר כיצד להמשיך.
                </p>
              </div>

              {error && (
                <div className={`${errorBox} mb-4`}>
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-3">
                {availableMfaTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleSelectMfaType(type)}
                    disabled={isSubmitting}
                    className="w-full flex items-center gap-3 py-3 px-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 text-sm transition-colors disabled:opacity-50"
                  >
                    {type === 'EMAIL_OTP' ? (
                      <Mail className="h-4 w-4 text-[var(--accent)]" />
                    ) : type === 'SMS_MFA' ? (
                      <Smartphone className="h-4 w-4 text-[var(--accent)]" />
                    ) : (
                      <ShieldCheck className="h-4 w-4 text-[var(--accent)]" />
                    )}
                    <span className="flex-1 text-right">{MFA_TYPE_LABELS[type] || type}</span>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  </button>
                ))}
              </div>

              <button type="button" onClick={() => resetToCredentials()} className={`${linkBtn} mt-4`}>
                בטל וחזור לכניסה
              </button>
            </>
          )}

          {/* Step 3 — TOTP setup (first enrollment) */}
          {step === 'mfa-setup' && (
            <>
              <div className="text-center mb-5">
                <div className={stepIconWrap}>
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">הפעלת אימות דו-שלבי</h2>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  סרוק את ה-QR באפליקציית Authenticator (Google Authenticator / Authy / 1Password / Microsoft Authenticator)
                  ולאחר מכן הזן את הקוד הראשון.
                </p>
              </div>

              {associating ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--accent)] border-t-transparent" />
                </div>
              ) : (
                <>
                  {otpauthUrl ? (
                    <div className="flex flex-col items-center mb-4">
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <QRCodeCanvas value={otpauthUrl} size={176} includeMargin={false} />
                      </div>
                    </div>
                  ) : null}

                  {secretCode ? (
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        קוד סודי לגיבוי (אם לא ניתן לסרוק)
                      </label>
                      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <code className="flex-1 text-xs text-gray-800 font-mono break-all select-all" dir="ltr">
                          {secretCode}
                        </code>
                        <button
                          type="button"
                          onClick={copySecret}
                          className="flex-shrink-0 text-gray-500 hover:text-gray-800"
                          aria-label="העתק קוד סודי"
                        >
                          {secretCopied ? <Check className="h-4 w-4 text-success-600" /> : <Copy className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                        שמור את הקוד במנהל סיסמאות. תזדקק לו רק אם אבד הטלפון.
                      </p>
                    </div>
                  ) : null}

                  <form onSubmit={handleMfaSetup} className="space-y-4" noValidate>
                    <div>
                      <label htmlFor="setup-code" className={labelClass}>
                        קוד מהאפליקציה (6 ספרות)
                      </label>
                      <input
                        ref={codeInputRef}
                        id="setup-code"
                        type="text"
                        value={mfaCode}
                        onChange={(e) => {
                          setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                          setError('');
                        }}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="\d{6}"
                        maxLength={6}
                        dir="ltr"
                        className={codeInputClass}
                        placeholder="••••••"
                      />
                    </div>

                    {error && (
                      <div className={errorBox}>
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}

                    <button type="submit" disabled={isSubmitting || mfaCode.length !== 6 || !otpauthUrl} className={primaryBtn}>
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          <span>מאמת...</span>
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="h-4 w-4" />
                          <span>אמת והפעל</span>
                        </>
                      )}
                    </button>

                    <button type="button" onClick={() => resetToCredentials()} className={linkBtn}>
                      בטל וחזור לכניסה
                    </button>
                  </form>
                </>
              )}
            </>
          )}

          {/* Step 4 — NEW_PASSWORD_REQUIRED */}
          {step === 'new-password' && (
            <>
              <div className="text-center mb-5">
                <div className={stepIconWrap}>
                  <Lock className="h-6 w-6" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">שינוי סיסמה ראשונה</h2>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  זוהי כניסתך הראשונה. צור סיסמה חדשה — לפחות 8 תווים, כולל אות גדולה, מספר וסימן.
                </p>
              </div>

              <form onSubmit={handleNewPassword} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="new-pwd" className={labelClass}>
                    סיסמה חדשה
                  </label>
                  <input
                    id="new-pwd"
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setError('');
                    }}
                    autoComplete="new-password"
                    minLength={8}
                    className={inputClass}
                    placeholder="סיסמה חזקה"
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="new-pwd-confirm" className={labelClass}>
                    אישור סיסמה
                  </label>
                  <input
                    id="new-pwd-confirm"
                    type="password"
                    value={newPasswordConfirm}
                    onChange={(e) => {
                      setNewPasswordConfirm(e.target.value);
                      setError('');
                    }}
                    autoComplete="new-password"
                    minLength={8}
                    className={inputClass}
                    placeholder="הזן שוב"
                  />
                </div>

                {error && (
                  <div className={errorBox}>
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || newPassword.length < 8 || newPassword !== newPasswordConfirm}
                  className={primaryBtn}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>שומר...</span>
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4" />
                      <span>שמור והתחבר</span>
                    </>
                  )}
                </button>

                <button type="button" onClick={() => resetToCredentials()} className={linkBtn}>
                  בטל וחזור לכניסה
                </button>
              </form>
            </>
          )}

          {/* Palette — re-colors the interface (saved client-side). */}
          <div
            className="mt-8 pt-5 border-t border-gray-100 flex items-center justify-center gap-3"
            role="radiogroup"
            aria-label="צבע הממשק"
          >
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <Palette className="h-3.5 w-3.5" />
              פלטה
            </span>
            <div className="flex items-center gap-2">
              {bgOptions.map((opt, idx) => {
                const isActive = opt.id === bgColor;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setBgColor(opt.id)}
                    onKeyDown={(e) => handleSwatchKeyDown(e, idx)}
                    role="radio"
                    aria-checked={isActive}
                    aria-label={opt.label}
                    title={opt.label}
                    tabIndex={isActive ? 0 : -1}
                    className={`relative h-7 w-7 rounded-full border border-black/10 transition-[transform,box-shadow] duration-200 ease-out-emil focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 ${
                      isActive ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: opt.swatch }}
                  >
                    {isActive && (
                      <Check className="absolute inset-0 m-auto h-3.5 w-3.5 text-white drop-shadow" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-center text-gray-400 text-xs mt-6">Powered by W3 Platform</p>
        </div>
      </div>
    </div>
  );
};

export default W3LoginPage;
